import React from 'react'
import Head from 'next/head'
import { useRouter } from 'next/router'

import { Dialog, DialogContent, DialogOverlay } from '@wartek-id/dialog'
import { Text } from '@wartek-id/text'
import { Spinner } from '@wartek-id/spinner'

import Header from 'app/BuktiKaryaPage/components/Header'
import PDFPreviewer from 'app/BuktiKaryaPage/components/PDFPreviewer'
import { useBCKSDesktopView } from 'app/BCKSPage/hooks/useBCKSDesktopView'
import { PageContainer, DesktopPageHeader } from 'components/ResponsiveLayout'
import { URL_HELPDESK } from 'app/BCKSPage/constants'
import { documentMap } from './guideData'

type GuidePreviewProps = {
  documentName?: string
}

export const BCKSInvitationGuidePreview = (props: GuidePreviewProps) => {
  const router = useRouter()
  const { documentName = null } = props
  const [pageLoaded, setPageLoaded] = React.useState(false)
  const [fileIsLoading, setFileIsLoading] = React.useState(true)
  const { title = '', docUrl = '', signedDocUrl = false } = router.query

  let docUrlSigned = docUrl

  try {
    if (!documentName && docUrl && signedDocUrl) {
      // add more if using route query
      const nonSignedQuery = ['title', 'docUrl', 'signedDocUrl']
      const additionalSignedUrlQuery = Object.keys(router.query).filter(
        (key) => !nonSignedQuery.includes(key)
      )
      const additionalSignedUrlQueryStr = additionalSignedUrlQuery
        .map((key) => `${key}=${router.query[key]}`)
        .join('&')
      docUrlSigned = `${docUrl}&${additionalSignedUrlQueryStr}`
    }
  } catch (error) {
    console.error(error)
  }

  const { isDesktop } = useBCKSDesktopView()

  const desktopPageLabel = documentName
    ? documentMap[documentName]?.title || 'Panduan'
    : title
    ? String(title)
    : 'Panduan'

  React.useEffect(() => {
    const onPageLoad = () => {
      setPageLoaded(true)
    }

    // Check if the page has already loaded
    if (document.readyState === 'complete') {
      onPageLoad()
    } else {
      window.addEventListener('load', onPageLoad)
      // Remove the event listener when component unmounts
      return () => window.removeEventListener('load', onPageLoad)
    }
  }, [])

  if (isDesktop) {
    const pageTitle = title
      ? String(title)
      : documentMap[documentName]?.title || 'Panduan'

    return (
      <div className="min-h-screen bg-surface-subdued">
        <Head>
          <title>{pageTitle}</title>
        </Head>
        <DesktopPageHeader
          breadcrumbItems={[
            { label: 'Seleksi KS', href: '/bcks/invitation/final' },
            { label: 'Panduan Dokumen', href: '/bcks/invitation/guide' },
            { label: desktopPageLabel, href: null },
          ]}
          navLinks={[{ label: 'Bantuan', href: URL_HELPDESK, external: true }]}
        />
        <PageContainer className="!pt-6 !pb-2">
          <Text as="h1" variant="heading-lg">
            {pageTitle}
          </Text>
        </PageContainer>
        <main>
          <PageContainer>
            <div className="max-w-5xl mx-auto">
              <Dialog isOpen={fileIsLoading} onClose={() => {}}>
                <DialogOverlay />
                <DialogContent className="max-w-sm m-auto">
                  <div className="flex flex-row items-center p-4">
                    <Spinner size="lg" />
                    <Text as="span" className="pl-4" variant="body">
                      Harap Tunggu, Tinjauan File Sedang Diproses
                    </Text>
                  </div>
                </DialogContent>
              </Dialog>
              <PDFPreviewer
                data={docUrlSigned || documentMap[documentName]?.src}
                onFilePreviewClick={() => null}
                pageLoaded={pageLoaded}
                setFileIsLoading={setFileIsLoading}
                isRemoteUrl
              />
            </div>
          </PageContainer>
        </main>
      </div>
    )
  }

  return (
    <div className="relative flex flex-col h-screen max-w-xl mx-auto">
      <Header
        title={title || documentMap[documentName]?.title}
        forceNavigateBack
      />
      <Dialog isOpen={fileIsLoading} onClose={() => {}}>
        <DialogOverlay />
        <DialogContent className="max-w-sm m-auto">
          <div className="flex flex-row items-center p-4">
            <Spinner size="lg" />
            <Text as="span" className="pl-4" variant="body">
              Harap Tunggu, Tinjauan File Sedang Diproses
            </Text>
          </div>
        </DialogContent>
      </Dialog>
      <PDFPreviewer
        data={docUrlSigned || documentMap[documentName]?.src}
        onFilePreviewClick={() => null}
        pageLoaded={pageLoaded}
        setFileIsLoading={setFileIsLoading}
        isRemoteUrl
      />
    </div>
  )
}
